<template>
    <div class="pie-wrap" v-if="slices.length">
        <div class="pie-svg">
            <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" role="img" aria-label="Gráfico de pizza">
                <!-- fundo -->
                <circle :cx="cx" :cy="cy" :r="r" class="pie-bg" />

                <!-- fatias -->
                <path
                    v-for="(s, idx) in slices"
                    :key="idx"
                    :d="s.d"
                    :fill="s.color"
                    class="pie-slice"
                />

                <!-- furo central (estilo donut) -->
                <circle :cx="cx" :cy="cy" :r="innerR" class="pie-hole" />

                <!-- total ao centro -->
                <text :x="cx" :y="cy - 2" text-anchor="middle" class="pie-total">
                    {{ formatBRL(total) }}
                </text>
                <text :x="cx" :y="cy + 14" text-anchor="middle" class="pie-sub">
                    Total
                </text>
            </svg>
        </div>

        <div class="pie-legend">
            <div class="legend-row" v-for="(s, idx) in slices" :key="`l-${idx}`">
                <span class="dot" :style="{ background: s.color }"></span>
                <div class="legend-main">
                    <div class="legend-title">{{ s.label }}</div>
                    <div class="legend-sub">
                        <span class="val">{{ formatBRL(s.value) }}</span>
                        <span class="pct">({{ s.pct.toFixed(1).replace('.', ',') }}%)</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="empty">
        Nenhuma despesa encontrada no período.
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    // [{ label: 'COMBUSTIVEL', value: 4557 }, ...]
    data: { type: Array, default: () => [] },
    size: { type: Number, default: 220 },
});

const size = computed(() => props.size);
const cx = computed(() => size.value / 2);
const cy = computed(() => size.value / 2);
const r = computed(() => Math.floor(size.value * 0.42));
const innerR = computed(() => Math.floor(r.value * 0.62));

const total = computed(() =>
    (props.data || []).reduce((acc, x) => acc + Number(x.value || 0), 0)
);

const palette = [
    '#3c5d5d', '#f2c94c', '#6fcf97', '#56ccf2', '#bb6bd9',
    '#eb5757', '#f2994a', '#2d9cdb', '#27ae60', '#9b51e0',
];

const formatBRL = (v) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
};

const polarToCartesian = (cx, cy, radius, angleDeg) => {
    const angleRad = (Math.PI / 180) * (angleDeg - 90);
    return {
        x: cx + radius * Math.cos(angleRad),
        y: cy + radius * Math.sin(angleRad),
    };
};

const describeArc = (cx, cy, radius, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
        'M', cx, cy,
        'L', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        'Z',
    ].join(' ');
};

const slices = computed(() => {
    const ls = (props.data || [])
        .map(x => ({ label: x.label, value: Number(x.value || 0) }))
        .filter(x => x.value > 0);

    const sum = ls.reduce((a, b) => a + b.value, 0);
    if (!sum) return [];

    let angle = 0;
    return ls.map((x, i) => {
        const pct = (x.value / sum) * 100;
        const sliceAngle = (x.value / sum) * 360;

        const start = angle;
        const end = angle + sliceAngle;
        angle += sliceAngle;

        return {
            label: x.label,
            value: x.value,
            pct,
            d: describeArc(cx.value, cy.value, r.value, start, end),
            color: palette[i % palette.length],
        };
    });
});
</script>

<style scoped>
.pie-wrap{
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 12px;
    align-items: center;
}

.pie-svg{
    display: flex;
    justify-content: center;
    align-items: center;
}

.pie-bg{
    fill: #f6fbfb;
    stroke: #dde7e7;
    stroke-width: 2;
}

.pie-slice{
    opacity: 0.95;
}

.pie-hole{
    fill: #fff;
    stroke: #dde7e7;
    stroke-width: 2;
}

.pie-total{
    font-size: 14px;
    font-weight: 800;
    fill: #3c5d5d;
}

.pie-sub{
    font-size: 12px;
    fill: #657979;
    font-weight: 600;
}

.pie-legend{
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.legend-row{
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border: 1px solid #dde7e7;
    border-radius: 8px;
    background: #fff;
    text-align: left;
}

.dot{
    width: 10px;
    height: 10px;
    border-radius: 999px;
    border: 1px solid #3c5d5d;
    flex-shrink: 0;
}

.legend-title{
    font-weight: 600;
    color: #3c5d5d;
}

.legend-sub{
    display: flex;
    gap: 6px;
    font-size: 12px;
    color: #657979;
    margin-top: 2px;
}

.val{
    font-weight: 700;
    color: #3c5d5d;
}

.pct{
    font-weight: 700;
}

.empty{
    padding: 12px;
    color: #657979;
}
</style>
